import type Parse from 'parse';

import { Analysis } from '@telespot/domain';
import { AnalysisTypeTopology, PipelineTopology } from '../protocols';
import { ParseMapper } from '../../parse.mapper';
import { AssetTopology, SampleTopology } from '../acquisition';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';
import { AlgorithmTopology } from '../ai';

export enum AnalysisTopology {
  TABLE = 'Analysis',
  SAMPLE = 'sample',
  ASSET = 'asset',
  CREATED_BY = 'createdBy',
  ASSET_STATUS = 'assetStatus',
  PIPELINE = 'pipeline',
  UUID = 'uuid',
  DATA = 'data',
  TYPE = 'analysisType',
  ALGORITHM = 'algorithm',
  ORGANIZATION = 'organization',
  ERROR = 'error',
}

export class ParseAnalysisMapper extends ParseMapper {
  public fromDomain(analysis: Analysis): Parse.Object {
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);
    const ParseAsset = this.subclasses.getSubclass(AssetTopology.TABLE);
    const ParsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE);
    const ParseAnalysisType = this.subclasses.getSubclass(AnalysisTypeTopology.TABLE);
    const ParseAlgorithm = this.subclasses.getSubclass(AlgorithmTopology.TABLE);

    const parseObject = this.parse.Object.fromJSON({
      className: AnalysisTopology.TABLE,
      [ObjectTopology.ID]: analysis.id,
      [ObjectTopology.CREATED_AT]: analysis.createdAt,
      [ObjectTopology.UPDATED_AT]: analysis.updatedAt,
    });

    parseObject.set({
      [SoftDeletableTopology.DELETED_AT]: analysis.deletedAt,
      [AnalysisTopology.SAMPLE]: analysis.sampleId ? ParseSample.createWithoutData(analysis.sampleId) : undefined,
      [AnalysisTopology.ASSET_STATUS]: analysis.assetStatus,
      [AnalysisTopology.CREATED_BY]: analysis.creatorIs('user')
        ? this.parse.User.createWithoutData(analysis.creatorId)
        : undefined,
      [AnalysisTopology.ALGORITHM]: analysis.creatorIs('algorithm')
        ? ParseAlgorithm.createWithoutData(analysis.creatorId)
        : undefined,
      [AnalysisTopology.ASSET]: analysis.assetId ? ParseAsset.createWithoutData(analysis.assetId) : undefined,
      [AnalysisTopology.PIPELINE]: analysis.pipelineId
        ? ParsePipeline.createWithoutData(analysis.pipelineId)
        : undefined,
      [AnalysisTopology.UUID]: analysis.uuid,
      [AnalysisTopology.DATA]: analysis.data,
      [AnalysisTopology.TYPE]: analysis.analysisType
        ? ParseAnalysisType.createWithoutData(analysis.analysisType.id)
        : undefined,
    });

    if (analysis.error) {
      parseObject.set(AnalysisTopology.ERROR, analysis.error);
    } else {
      parseObject.unset(AnalysisTopology.ERROR);
    }

    return parseObject;
  }

  public toDomain(object: Parse.Object): Analysis {
    const parseCreatedBy = object.get(AnalysisTopology.CREATED_BY);
    const parseAlgorithm = object.get(AnalysisTopology.ALGORITHM);

    const creatorEntity = parseAlgorithm ? 'algorithm' : 'user';
    const creatorId = creatorEntity === 'algorithm' ? parseAlgorithm?.id : parseCreatedBy?.id;

    return new Analysis({
      id: object.id,
      organizationId: object.get(AnalysisTopology.ORGANIZATION)?.id,
      sampleId: object.get(AnalysisTopology.SAMPLE)?.id,
      assetId: object.get(AnalysisTopology.ASSET)?.id,
      creatorEntity,
      creatorId,
      assetStatus: object.get(AnalysisTopology.ASSET_STATUS),
      pipelineId: object.get(AnalysisTopology.PIPELINE)?.id,
      uuid: object.get(AnalysisTopology.UUID),
      deletedAt: object.get(SoftDeletableTopology.DELETED_AT),
      createdAt: object.get(ObjectTopology.CREATED_AT),
      updatedAt: object.get(ObjectTopology.UPDATED_AT),
      data: object.get(AnalysisTopology.DATA),
      error: object.get(AnalysisTopology.ERROR),
    });
  }
}
